import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useFilters } from '../context/FilterContext';
import type { VettingItem } from '../types';

export function VettingPage() {
  const [items, setItems] = useState<VettingItem[]>([]);
  const [filter, setFilter] = useState('all');
  const { filters } = useFilters();

  useEffect(() => {
    api.vetting().then(setItems).catch(console.error);
  }, []);

  const filtered = useMemo(() => items.filter((v) => {
    if (filters.policyAreaId && v.policy_area_id !== filters.policyAreaId) return false;
    if (filters.variableId && v.variable_id !== filters.variableId) return false;
    if (filter === 'off-track') return v.scenario_position === 'Below Incremental' || v.scenario_position === 'DATA REQUIRED';
    if (filter === 'gdp-drivers') return v.variable_id.startsWith('PM_') || v.variable_id.startsWith('CORE_');
    return true;
  }).slice(0, 100), [items, filter, filters]);

  return (
    <div>
      <h1 className="page-title">Vetting & Progress</h1>
      <p className="page-subtitle">Latest official observation for each indicator against the four scenario benchmarks. Every value shows its source agency and period; sources are managed on the Data & Evidence page.</p>

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <select className="field" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">All indicators</option>
          <option value="off-track">Off-track / missing data</option>
          <option value="gdp-drivers">Core / PM variables</option>
        </select>
        {(filters.policyAreaId || filters.variableId) && (
          <span style={{ fontSize: '0.85rem', color: '#6b7280', alignSelf: 'center' }}>
            Cross-filter active — {filtered.length} matching rows
          </span>
        )}
      </div>

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Variable</th><th>Area</th><th>Actual</th><th>Incremental</th><th>Moderate</th><th>Accelerated</th><th>Transformational</th><th>Position</th><th>Attainment</th><th>Source · period</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => (
              <tr key={v.variable_id} className={filters.variableId === v.variable_id ? 'row-highlight' : undefined}>
                <td>{v.variable_name.slice(0, 35)}</td>
                <td>{v.policy_area_name}</td>
                <td>{v.actual_value ?? '—'}</td>
                <td>{v.incremental ?? '—'}</td>
                <td>{v.moderate ?? '—'}</td>
                <td>{v.accelerated ?? '—'}</td>
                <td>{v.transformational ?? '—'}</td>
                <td>{v.scenario_position}</td>
                <td>{v.attainment_pct != null ? `${(v.attainment_pct * 100).toFixed(0)}%` : '—'}</td>
                <td style={{ fontSize: '0.75rem', color: '#6b7280' }}>{v.source_agency ? <>{v.source_url ? <a href={v.source_url} target="_blank" rel="noreferrer">{v.source_agency}</a> : v.source_agency}<div>{v.observation_date}{v.source_note && v.source_note.toLowerCase().includes('auto-fetch') ? ' · API' : ''}</div></> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
